import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Box,
  Button,
  Divider,
  GlobalStyles,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { MapEvent, MapProps, useMap } from '@vis.gl/react-google-maps';
import moment from 'moment';
import { useDebouncedCallback } from 'use-debounce';

// Internal imports (@/)
import { AddressApi, CfsApi, MapLayerApi } from '@/api';
import {
  IcDeadAnimalPin,
  IcDebrisPin,
  IcDeleteTrash,
  IcDownPolePin,
  IcDownWiresPin,
  IcEdit,
  IcFireHydrantColorPin,
  IcFireHydrantPin,
  IcFloodingPin,
  IcLimitedVisibilityPin,
  IcLocationMarker,
  IcMarkerCfs,
  IcOpenManholePin,
  IcPotHolePin,
  IcRoadBlockPin,
  IcRoadWorkPin,
} from '@/assets/images';
import { MapBreadcrumbItem } from '@/components/GoogleMap/MapBreadcrumb';
import { CFSAddressJurisdictionModal } from '@/components/Modals';
import { ASSIGNMENT_STATUS } from '@/constants';
import { getMarkerTypeKey } from '@/constants/mapMarkerKeys';
import { useCFSContext, useUnitShiftContext } from '@/hooks';
import { useMapMarkerTypes } from '@/hooks/useMapMarkerTypes';
import { useMarkerTypes } from '@/hooks/useMarkerTypes';
import {
  AssignmentStatus,
  ILocationPoint,
  IPointLayer,
  IUser,
  PointLayerType,
  PointLayerTypeLabel,
  IHydrantProperties,
  IAddressForm,
  IAgency,
  AssignedUnitShiftStatus,
  FireHydrantTapeColor,
  IPoint,
} from '@/models';
import { CFSAddressChangeModal } from '@/pages/CFS/Edit/components/CFSAddressChangeModal';
import {
  googlePlaceService,
  isDispatcherRole,
  isResolvableAddress,
} from '@/services';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { isInsideMultiPolygon, openNewWindow } from '@/utils';

import FireHydrantPopup from './FireHydrantPopup';
import { GoogleMap, MapBounds } from './GoogleMap';
import { IMarker } from './MapMarker';
import { MapMarkerControl } from './MapMarkerControl';
import { MapTypeControl } from './MapTypeControl';
import { ConfirmationDialog } from '../Dialogs';
import CreateNewMarkerModal from '../Modals/CreateNewMarkerModal/CreateNewMarkerModal';

interface GoogleMapProps extends Omit<MapProps, 'center'>, PropsWithChildren {
  center?: google.maps.LatLngLiteral;
  markers?: IMarker[];
  breadcrumb?: MapBreadcrumbItem[];
  onHandleMapTypeIdChange?: (mapTypeId?: string) => void;
  showContextMenu?: boolean;
  showStreetViewControl?: boolean;
  onCloseMarkerInfo?: (key: string) => void;
  showZoomControl?: boolean;
  className?: string;
  isFirstResponderView?: boolean;
  selectedMarkerKey?: string | null;
  onMarkerClick?: (markerKey: string | null) => void;
}

function isExpired(time?: number) {
  if (!time) {
    return false;
  }
  return time < moment().unix();
}

export const FireHydrantMap: FC<GoogleMapProps> = (props) => {
  const {
    markers = [],
    breadcrumb,
    onHandleMapTypeIdChange,
    showContextMenu,
    showStreetViewControl,
    onCloseMarkerInfo,
    showZoomControl = true,
    className,
    isFirstResponderView,
    ...rest
  } = props;
  const [queryParams] = useSearchParams();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [showConfirmLocation, setShowConfirmLocation] =
    useState<boolean>(false);
  const [hydrantPointMarkers, setHydrantPointMarkers] = useState<IMarker[]>([]);
  const [potholePointMarkers, setPotholePointMarkers] = useState<IMarker[]>([]);
  const [roadBlockPointMarkers, setRoadBlockPointMarkers] = useState<IMarker[]>(
    [],
  );
  const [floodingPointMarkers, setFloodingPointMarkers] = useState<IMarker[]>(
    [],
  );
  const [limitedVisibilityPointMarkers, setLimitedVisibilityPointMarkers] =
    useState<IMarker[]>([]);
  const [deadAnimalPointMarkers, setDeadAnimalPointMarkers] = useState<
    IMarker[]
  >([]);
  const [roadWorkPointMarkers, setRoadWorkPointMarkers] = useState<IMarker[]>(
    [],
  );
  const [downWiresPointMarkers, setDownWiresPointMarkers] = useState<IMarker[]>(
    [],
  );
  const [downPolePointMarkers, setDownPolePointMarkers] = useState<IMarker[]>(
    [],
  );
  const [debrisPointMarkers, setDebrisPointMarkers] = useState<IMarker[]>([]);
  const [openManholePointMarkers, setOpenManholePointMarkers] = useState<
    IMarker[]
  >([]);
  const [locationPointMarkers, setLocationPointMarkers] = useState<IMarker[]>(
    [],
  );
  const [customPointMarkers, setCustomPointMarkers] = useState<IMarker[]>([]);
  const { ablyCfsChannel, updateCFS, cfs } = useCFSContext();
  const { updateToast } = useToastStore();

  const { selectedTypes, handleCheckboxChange } = useMapMarkerTypes();
  const { markerTypes } = useMarkerTypes();
  const [newMarkerModal, setNewMarkerModal] = useState<boolean>(false);
  const [editMarker, setEditMarker] = useState<IPointLayer | null>(null);
  const [addressMarker, setAddressMarker] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [pendingRemovedAgencies, setPendingRemovedAgencies] = useState<
    IAgency[]
  >([]);
  const [openAddressChangeModal, setOpenAddressChangeModal] = useState(false);
  const [openAddressBoundaryModal, setOpenAddressBoundaryModal] =
    useState(false);
  const [placeDetails, setPlaceDetails] = useState<any>();
  const [pendingLocation, setPendingLocation] = useState<{
    addressId: string | null;
    locationPoints: Partial<ILocationPoint>[];
  } | null>(null);
  const [isSubmittingKeepAddress, setIsSubmittingKeepAddress] = useState(false);
  const isClickingKeepAddress = useRef(false);

  const mapTypeId = queryParams.get('mapTypeId') || 'roadmap';
  const map = useMap();
  const { user, account } = useUserStore();
  const theme = useTheme();
  const { fetchAssignedUnitShifts } = useUnitShiftContext();
  useEffect(() => {
    // Configure the click listener.
    if (map && isDispatcherRole(user as IUser)) {
      map?.addListener(
        'rightclick',
        async (mapsMouseEvent: google.maps.MapMouseEvent) => {
          // const { lat, lng } = mapsMouseEvent.latLng ?? {};
          const lat = mapsMouseEvent.latLng?.lat();
          const lng = mapsMouseEvent.latLng?.lng();

          if (lat && lng) {
            let place: google.maps.places.PlaceResult;
            try {
              place = await googlePlaceService.geocodeCoordinates(lat, lng);
            } catch {
              // No geocode result at all (open water, etc.) — still allow the pin.
              place = {};
            }

            if (place.geometry && place.geometry.location) {
              place.geometry.location.lat = () => lat;
              place.geometry.location.lng = () => lng;
            } else {
              place.geometry = {
                location: {
                  lat: () => lat,
                  lng: () => lng,
                } as google.maps.LatLng,
              };
            }

            if (!place.formatted_address) {
              place.formatted_address = `${lat}, ${lng}`;
            }

            setAddressMarker(place);
          }
        },
      );
    }
  }, [map]);

  const handleCancelAddressChange = () => {
    setPendingRemovedAgencies([]);
    setOpenAddressChangeModal(false);
  };

  const applyCfsLocation = async (location: {
    addressId: string | null;
    locationPoints: Partial<ILocationPoint>[];
  }) => {
    if (!cfs?._id) {
      return;
    }
    try {
      const res = await CfsApi.updateAddressInfo(cfs._id, location.addressId, {
        locationPoints: location.locationPoints,
      });
      await ablyCfsChannel?.publish('cfsForm', {
        addressInfo: {
          ...cfs?.addressInfo,
          ...res.data.addressInfo,
        },
      });
      updateCFS((prev) =>
        prev ? { ...prev, addressInfo: res.data.addressInfo } : prev,
      );
      setAddressMarker(null);
      setPendingLocation(null);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  // The dispatcher's exact pin, kept alongside any manual Location points.
  const buildPinLocationPoints = (
    marker: google.maps.places.PlaceResult,
  ): Partial<ILocationPoint>[] => {
    const lat = marker.geometry?.location?.lat() ?? 0;
    const lng = marker.geometry?.location?.lng() ?? 0;
    const existingPoints = (cfs?.addressInfo?.locationPoints ?? []).filter(
      (lp) => !lp.isPrimary,
    );
    return [
      ...existingPoints,
      {
        point: { type: 'Point', coordinates: [lng, lat] },
        name: (marker.formatted_address ?? `${lat}, ${lng}`).slice(0, 100),
        isPrimary: true,
      },
    ];
  };

  const handleNewAddress = async () => {
    if (!cfs?._id || !addressMarker) return;

    const pinPoint: IPoint = {
      type: 'Point',
      coordinates: [
        addressMarker.geometry?.location?.lng() ?? 0,
        addressMarker.geometry?.location?.lat() ?? 0,
      ],
    };
    const pinLocationPoints = buildPinLocationPoints(addressMarker);

    const googlePlaceServiceDetails = addressMarker.place_id
      ? await googlePlaceService
          .getPlaceDetails(addressMarker.place_id)
          .catch(() => null)
      : null;

    setPlaceDetails(googlePlaceServiceDetails);
    setAddressMarker(addressMarker);

    const hasResolvableAddress = isResolvableAddress(googlePlaceServiceDetails);

    let createdAddressId: string | null = null;
    if (hasResolvableAddress) {
      try {
        const addressResponse = await AddressApi.createAddress({
          ...googlePlaceServiceDetails,
          // Keep the Google-derived business name off the master record.
          businessName: undefined,
          point: pinPoint,
        });
        createdAddressId = addressResponse.data._id as string;
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return;
      }
    }

    const pendingPayload = {
      addressId: createdAddressId,
      locationPoints: pinLocationPoints,
    };
    setPendingLocation(pendingPayload);

    // Agency check (omit address for pin-only locations; null is rejected)
    const checkResponse = await CfsApi.checkAgenciesUpdate(cfs._id, {
      incidentCodes: cfs.incidentCodes.map((ic) => String(ic._id ?? ic)),
      ...(createdAddressId ? { address: createdAddressId } : {}),
    });

    setPendingRemovedAgencies(checkResponse.data);

    const affectedUnits = cfs.assignedUnitShifts.filter(
      (au) =>
        checkResponse.data
          .map((a) => a._id)
          .includes(au.unitShift.unit.agency) &&
        au.status !== AssignedUnitShiftStatus.CANCELED &&
        au.status !== AssignedUnitShiftStatus.COMPLETED,
    );

    const needsAgencyModal =
      checkResponse.data.length > 0 && affectedUnits.length > 0;

    if (!needsAgencyModal) {
      // Boundary check still applies — against the exact pin coordinates
      if (
        account?.settings.areas &&
        !isInsideMultiPolygon(pinPoint as any, account?.settings.areas)
      ) {
        setOpenAddressBoundaryModal(true);
        return;
      }
      await applyCfsLocation(pendingPayload);
    } else {
      setOpenAddressChangeModal(true);
    }
  };

  const handleUpdateAddress = async (
    address: IAddressForm | null,
    addressMarkerGeo: google.maps.places.PlaceResult | null,
  ) => {
    if (!cfs?._id) return;
    try {
      if (address && addressMarkerGeo) {
        const addressResponse = await AddressApi.createAddress({
          ...address,
          // Keep the Google-derived business name off the master record.
          businessName: undefined,
          point: {
            type: 'Point',
            coordinates: [
              addressMarkerGeo?.geometry?.location?.lng() ?? 0,
              addressMarkerGeo?.geometry?.location?.lat() ?? 0,
            ],
          },
        });

        try {
          const updateRes = await CfsApi.updateAddressInfo(
            cfs._id,
            addressResponse.data._id as string,
          );
          await ablyCfsChannel?.publish('cfsForm', {
            addressInfo: {
              ...cfs?.addressInfo,
              ...updateRes.data.addressInfo,
            },
          });
          if (cfs) {
            updateCFS({
              ...cfs,
              addressInfo: updateRes.data.addressInfo,
            });
          }
          setAddressMarker(null);
        } catch (err: any) {
          updateToast({ open: true, message: err.message });
        }
      }
    } catch (err: any) {
      return;
    }
  };

  const handleNewMarker = () => {
    if (addressMarker && addressMarker.place_id) {
      setNewMarkerModal(true);
    }
  };

  const handleNewLocationPoint = async () => {
    if (cfs && addressMarker && addressMarker.geometry?.location) {
      try {
        const res = await CfsApi.updateAddressInfo(
          cfs._id as string,
          cfs.addressInfo?.address?._id as string,
          {
            ...cfs.addressInfo,
            locationPoints: [
              ...(cfs.addressInfo?.locationPoints ?? []).filter(
                (lp) => lp.isPrimary,
              ),
              {
                point: {
                  type: 'Point',
                  coordinates: [
                    addressMarker.geometry?.location?.lng() ?? 0,
                    addressMarker.geometry?.location?.lat() ?? 0,
                  ],
                },
                name: addressMarker.formatted_address?.slice(0, 100),
              },
            ],
          },
        );
        updateCFS({
          ...cfs,
          addressInfo: res.data.addressInfo,
        });

        updateToast({
          open: true,
          type: ConfirmTypes.SUCCESS,
          message: 'Location Point added successfully.',
        });

        setAddressMarker(null);
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    }
  };

  const deleteLocationPoint = async () => {
    if (cfs) {
      try {
        const res = await CfsApi.updateAddressInfo(
          cfs._id as string,
          cfs.addressInfo?.address?._id as string,
          {
            ...cfs.addressInfo,
            locationPoints: (cfs.addressInfo?.locationPoints ?? []).filter(
              (lp) => lp.isPrimary,
            ),
          },
        );

        updateCFS({
          ...cfs,
          addressInfo: res.data.addressInfo,
        });
        updateToast({
          open: true,
          type: ConfirmTypes.SUCCESS,
          message: 'Location Point removed successfully.',
        });
        setLocationPointMarkers([]);
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    }
  };

  const handleCfsCreate = async () => {
    if (addressMarker && addressMarker.geometry?.location) {
      try {
        const address = addressMarker.place_id
          ? await googlePlaceService
              .getPlaceDetails(addressMarker.place_id)
              .catch(() => null)
          : null;

        let addressId: string | null = null;
        if (isResolvableAddress(address)) {
          const addressResponse = await AddressApi.createAddress({
            ...address,
            // Keep the Google-derived business name off the master record.
            businessName: undefined,
            point: {
              type: 'Point',
              coordinates: [
                addressMarker.geometry?.location?.lng() ?? 0,
                addressMarker.geometry?.location?.lat() ?? 0,
              ],
            },
          });
          addressId = addressResponse.data._id as string;
        }

        const response = await CfsApi.create();
        await CfsApi.updateAddressInfo(response.data._id as string, addressId, {
          locationPoints: buildPinLocationPoints(addressMarker),
        });
        await fetchAssignedUnitShifts();
        setAddressMarker(null);
        openNewWindow(`/cfs/${response.data._id}`, response.data._id);
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    }
  };

  const newCfsAddressMarker: IMarker | null = useMemo<IMarker | null>(() => {
    const isPinOnly =
      !!addressMarker &&
      (!addressMarker.place_id ||
        !addressMarker.address_components?.some((c) =>
          c.types.includes('postal_code'),
        ));

    return addressMarker
      ? {
          key: addressMarker.place_id ?? 'new-cfs-pin',
          maxWidth: 400,
          info: (
            <Box p={0.5}>
              <Typography fontWeight={500} fontSize={14}>
                {addressMarker.formatted_address}
              </Typography>
              <Typography fontWeight={400} fontSize={12} color="#007AFF">
                {addressMarker?.geometry?.location?.lat()} -
                {addressMarker?.geometry?.location?.lng()}
              </Typography>
              {isPinOnly && (
                <Typography
                  fontWeight={400}
                  fontSize={12}
                  color="text.secondary"
                >
                  No mailing address found — exact coordinates will be used
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" gap={1}>
                {cfs ? (
                  <Button
                    color="error"
                    size="small"
                    variant="contained"
                    onClick={handleNewAddress}
                  >
                    {isPinOnly ? 'Use as CFS Location' : 'Use as CFS Address'}
                  </Button>
                ) : (
                  <Button
                    color="error"
                    size="small"
                    variant="contained"
                    onClick={handleCfsCreate}
                  >
                    Create CFS
                  </Button>
                )}
                <Button
                  color="inherit"
                  variant="outlined"
                  size="small"
                  onClick={handleNewLocationPoint}
                  disabled={
                    (cfs?.addressInfo?.locationPoints?.filter(
                      (lp) => !lp.isPrimary,
                    ).length ?? 0) > 0
                  }
                >
                  Location
                </Button>
                <Button
                  color="inherit"
                  variant="outlined"
                  size="small"
                  onClick={handleNewMarker}
                >
                  Marker
                </Button>
              </Stack>
            </Box>
          ),
          title: 'New address',
          position: {
            lng: addressMarker?.geometry?.location?.lng() ?? 0,
            lat: addressMarker?.geometry?.location?.lat() ?? 0,
          },
          icon: <IcMarkerCfs color={colors.warning.main} />,
          showInfo: true,
          toggleInfo: () => {
            setAddressMarker(null);
          },
        }
      : null;
  }, [addressMarker]);

  const getMarkerTypeLabel = (type: any): string => {
    if (typeof type === 'string') {
      return type;
    }
    return PointLayerTypeLabel[type as PointLayerType] || type;
  };

  const getMarkerTypeColor = (mtype: any): string => {
    if (typeof mtype === 'string') {
      const typeLower = mtype.toLowerCase();
      if (
        (Object.values(FireHydrantTapeColor) as string[]).includes(typeLower)
      ) {
        switch (typeLower) {
          case FireHydrantTapeColor.White:
          case FireHydrantTapeColor.Whitel:
            return '#FFFFFF';
          case FireHydrantTapeColor.Blue:
            return '#5586FF';
          case FireHydrantTapeColor.Green:
            return '#00C341';
          case FireHydrantTapeColor.Orange:
            return '#FF7331';
          case FireHydrantTapeColor.Yellow:
            return '#FFE100';
          case FireHydrantTapeColor.Black:
            return '#000000';
          default:
            return '';
        }
      }

      const markerType = markerTypes.find((mt) => mt.name === mtype);
      if (markerType && markerType.color) {
        return markerType.color;
      }

      const fallbackColors = [
        '#FF6B6B',
        '#4ECDC4',
        '#45B7D1',
        '#96CEB4',
        '#FFEAA7',
        '#DDA0DD',
        '#98D8C8',
        '#F7DC6F',
        '#BB8FCE',
        '#85C1E9',
      ];

      let hash = 0;
      for (let i = 0; i < mtype.length; i++) {
        hash = mtype.charCodeAt(i) + ((hash << 5) - hash);
      }
      const colorIndex = Math.abs(hash) % fallbackColors.length;
      const fallbackColor = fallbackColors[colorIndex];
      return fallbackColor;
    }
    return colors.error.main;
  };

  const getIconForType = (type: any, color: string) => {
    switch (type) {
      case PointLayerType.FIRE_HYDRANT:
        if (!color) {
          return <IcFireHydrantPin />;
        }
        return <IcFireHydrantColorPin color={color} />;
      case PointLayerType.ROAD_BLOCK:
        return <IcRoadBlockPin />;
      case PointLayerType.FLOODING:
        return <IcFloodingPin />;
      case PointLayerType.LIMITED_VISIBILITY:
        return <IcLimitedVisibilityPin />;
      case PointLayerType.POTHOLE:
        return <IcPotHolePin />;
      case PointLayerType.DEAD_ANIMAL:
        return <IcDeadAnimalPin />;
      case PointLayerType.ROAD_WORK:
        return <IcRoadWorkPin />;
      case PointLayerType.DOWN_WIRES:
        return <IcDownWiresPin />;
      case PointLayerType.DOWN_POLE:
        return <IcDownPolePin />;
      case PointLayerType.DEBRIS:
        return <IcDebrisPin />;
      case PointLayerType.OPEN_MANHOLE:
        return <IcOpenManholePin />;
    }

    if (typeof type === 'string') {
      switch (type) {
        case 'Fire Hydrant':
          return <IcFireHydrantPin />;
        case 'Road Block':
          return <IcRoadBlockPin />;
        case 'Flooding':
          return <IcFloodingPin />;
        case 'Limited Visibility':
          return <IcLimitedVisibilityPin />;
        case 'Pothole':
          return <IcPotHolePin />;
        case 'Dead Animal':
          return <IcDeadAnimalPin />;
        case 'Road Work':
          return <IcRoadWorkPin />;
        case 'Down Wires':
          return <IcDownWiresPin />;
        case 'Down Pole':
          return <IcDownPolePin />;
        case 'Debris':
          return <IcDebrisPin />;
        case 'Open Manhole':
          return <IcOpenManholePin />;
        default:
          return <IcLocationMarker color={color} />;
      }
    }

    return <IcFireHydrantPin />;
  };

  const handleEditMarker = (pointLayer: IPointLayer) => {
    setEditMarker(pointLayer);
    setNewMarkerModal(true);
  };

  const handleDeleteMarker = (pointLayer: IPointLayer) => {
    if (pointLayer) {
      MapLayerApi.deletePointLayer(pointLayer._id as string)
        .then(() => {
          updateToast({
            open: true,
            type: ConfirmTypes.SUCCESS,
            message: 'Marker deleted successfully.',
          });
        })
        .catch((err: any) => {
          updateToast({ open: true, message: err.message });
        });
    }
  };

  const fetchMapPoints = async (bounds: MapBounds) => {
    try {
      const hydrantMarkers: IMarker[] = [];
      const potHoleMarkers: IMarker[] = [];
      const roadBlockMarkers: IMarker[] = [];
      const floodingMarkers: IMarker[] = [];
      const limitedVisibilityMarkers: IMarker[] = [];
      const deadAnimalMarkers: IMarker[] = [];
      const downWiresMarkers: IMarker[] = [];
      const downPoleMarkers: IMarker[] = [];
      const debrisMarkers: IMarker[] = [];
      const openManholeMarkers: IMarker[] = [];
      const roadWorkMarkers: IMarker[] = [];
      const customMarkers: IMarker[] = [];

      const params = {
        page: 1,
        limit: 1000,
        bounds: JSON.stringify({
          north: bounds.north,
          east: bounds.east,
          south: bounds.south,
          west: bounds.west,
        }),
      };

      const res = await MapLayerApi.getPointLayers(params);

      res.data.docs.forEach((pointLayer) => {
        const { _id, point, type, properties, status, updatedAt } = pointLayer;

        const m: IMarker = {
          key: String(_id),
          position: {
            lng: point.coordinates[0],
            lat: point.coordinates[1],
          },
          grayedOut: isExpired(properties?.expirationDateTime),
          isExpired: isExpired(properties?.expirationDateTime),
          icon: getIconForType(
            type,
            getMarkerTypeColor(properties?.tapeColor || type),
          ),
          type,
          properties,
          status,
          updatedAt,
          info: (
            <Stack>
              {pointLayer.isImported && type === PointLayerType.FIRE_HYDRANT ? (
                <FireHydrantPopup data={properties as IHydrantProperties} />
              ) : (
                <>
                  <Typography fontWeight={500} fontSize={14}>
                    {getMarkerTypeLabel(type)}
                  </Typography>
                  {(properties?.expirationDate ||
                    properties?.expirationTime) && (
                    <Typography color="text.secondary" fontSize={14}>
                      Until:{' '}
                      {properties.expirationDate &&
                        moment(properties.expirationDate, 'YYYY-MM-DD').format(
                          'MM/DD/YYYY',
                        )}
                      {properties.expirationDate && properties.expirationTime
                        ? ' '
                        : ''}
                      {properties.expirationTime &&
                        moment(properties.expirationTime, 'HH:mm').format(
                          'HH:mm',
                        )}
                    </Typography>
                  )}
                  {properties?.description && (
                    <Typography
                      color="text.secondary"
                      fontWeight={400}
                      fontSize={14}
                    >
                      {properties?.description}
                    </Typography>
                  )}
                  {properties?.address && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography color="text.secondary" fontSize={14}>
                        {properties.address}
                      </Typography>
                    </>
                  )}
                </>
              )}

              {isDispatcherRole(user as IUser) && !pointLayer.isImported && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" gap={1} justifyContent="flex-end">
                    <Button
                      variant="contained"
                      size="small"
                      color="error"
                      onClick={() => {
                        handleEditMarker(pointLayer);
                      }}
                      sx={{ minWidth: 'auto', p: '4px' }}
                    >
                      <IcEdit />
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          ),
        };

        if (type === PointLayerType.FIRE_HYDRANT) {
          hydrantMarkers.push(m);
        } else if (type === PointLayerType.POTHOLE) {
          potHoleMarkers.push(m);
        } else if (type === PointLayerType.ROAD_BLOCK) {
          roadBlockMarkers.push(m);
        } else if (type === PointLayerType.FLOODING) {
          floodingMarkers.push(m);
        } else if (type === PointLayerType.LIMITED_VISIBILITY) {
          limitedVisibilityMarkers.push(m);
        } else if (type === PointLayerType.DEAD_ANIMAL) {
          deadAnimalMarkers.push(m);
        } else if (type === PointLayerType.DOWN_WIRES) {
          downWiresMarkers.push(m);
        } else if (type === PointLayerType.DOWN_POLE) {
          downPoleMarkers.push(m);
        } else if (type === PointLayerType.DEBRIS) {
          debrisMarkers.push(m);
        } else if (type === PointLayerType.OPEN_MANHOLE) {
          openManholeMarkers.push(m);
        } else if (type === PointLayerType.ROAD_WORK) {
          roadWorkMarkers.push(m);
        } else {
          customMarkers.push(m);
        }
      });

      setHydrantPointMarkers(hydrantMarkers);
      setPotholePointMarkers(potHoleMarkers);
      setRoadBlockPointMarkers(roadBlockMarkers);
      setFloodingPointMarkers(floodingMarkers);
      setLimitedVisibilityPointMarkers(limitedVisibilityMarkers);
      setDeadAnimalPointMarkers(deadAnimalMarkers);
      setDownWiresPointMarkers(downWiresMarkers);
      setDownPolePointMarkers(downPoleMarkers);
      setDebrisPointMarkers(debrisMarkers);
      setOpenManholePointMarkers(openManholeMarkers);
      setRoadWorkPointMarkers(roadWorkMarkers);
      setCustomPointMarkers(customMarkers);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleMapTypeIdChange = (event: MapEvent) => {
    if (onHandleMapTypeIdChange) {
      onHandleMapTypeIdChange(event.map.getMapTypeId());
    }
  };

  const onNewMarkerAdded = (marker: IPointLayer) => {
    const m = {
      key: String(marker._id),
      position: {
        lng: marker.point.coordinates[0],
        lat: marker.point.coordinates[1],
      },
      grayedOut:
        marker.status === 'INACTIVE' ||
        isExpired(marker.properties?.expirationDateTime),
      isExpired: isExpired(marker.properties?.expirationDateTime),
      icon: getIconForType(marker.type, getMarkerTypeColor(marker.type)),
      type: marker.type,
      properties: marker.properties,
      status: marker.status,
      updatedAt: marker.updatedAt,
      isImported: marker.isImported,
      info: (
        <Stack>
          {marker.isImported && marker.type === PointLayerType.FIRE_HYDRANT ? (
            <FireHydrantPopup data={marker.properties as IHydrantProperties} />
          ) : (
            <>
              <Typography fontWeight={500} fontSize={14}>
                {getMarkerTypeLabel(marker.type)}
              </Typography>
              {(marker.properties?.expirationDate ||
                marker.properties?.expirationTime) && (
                <Typography color="text.secondary" fontSize={14}>
                  Until:{' '}
                  {marker.properties.expirationDate &&
                    moment(
                      marker.properties.expirationDate,
                      'YYYY-MM-DD',
                    ).format('MM/DD/YYYY')}
                  {marker.properties.expirationDate &&
                  marker.properties.expirationTime
                    ? ' '
                    : ''}
                  {marker.properties.expirationTime &&
                    moment(marker.properties.expirationTime, 'HH:mm').format(
                      'HH:mm',
                    )}
                </Typography>
              )}
              {marker.properties?.description && (
                <Typography
                  color="text.secondary"
                  fontWeight={400}
                  fontSize={14}
                >
                  {marker.properties?.description}
                </Typography>
              )}
              {marker.properties?.address && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography color="text.secondary" fontSize={14}>
                    {marker.properties.address}
                  </Typography>
                </>
              )}
            </>
          )}

          {isDispatcherRole(user as IUser) && marker.isImported === false && (
            <>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => {
                    handleEditMarker(marker);
                  }}
                  sx={{ minWidth: 'auto', p: '4px' }}
                >
                  <IcEdit />
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      ),
    };

    switch (marker.type) {
      case PointLayerType.FIRE_HYDRANT:
        setHydrantPointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.POTHOLE:
        setPotholePointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.ROAD_BLOCK:
        setRoadBlockPointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.FLOODING:
        setFloodingPointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.LIMITED_VISIBILITY:
        setLimitedVisibilityPointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.DEAD_ANIMAL:
        setDeadAnimalPointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.ROAD_WORK:
        setRoadWorkPointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.DOWN_WIRES:
        setDownWiresPointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.DOWN_POLE:
        setDownPolePointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.DEBRIS:
        setDebrisPointMarkers((prev) => [...prev, m]);
        break;
      case PointLayerType.OPEN_MANHOLE:
        setOpenManholePointMarkers((prev) => [...prev, m]);
        break;
      default:
        // Handle custom marker types
        setCustomPointMarkers((prev) => [...prev, m]);
        break;
    }
  };

  const handleBoundMapChange = useDebouncedCallback(
    (bounds: MapBounds, zoom: number) => {
      if (zoom > 15) {
        fetchMapPoints(bounds);
      } else {
        setHydrantPointMarkers([]);
      }
    },
    500,
  );

  useEffect(() => {
    if (markerTypes.length > 0) {
      const updateMarkerColors = (markerList: IMarker[]) => {
        return markerList.map((marker) => ({
          ...marker,
          icon: getIconForType(marker.type, getMarkerTypeColor(marker.type)),
        }));
      };

      setHydrantPointMarkers((prev) => updateMarkerColors(prev));
      setPotholePointMarkers((prev) => updateMarkerColors(prev));
      setRoadBlockPointMarkers((prev) => updateMarkerColors(prev));
      setFloodingPointMarkers((prev) => updateMarkerColors(prev));
      setLimitedVisibilityPointMarkers((prev) => updateMarkerColors(prev));
      setDeadAnimalPointMarkers((prev) => updateMarkerColors(prev));
      setDownWiresPointMarkers((prev) => updateMarkerColors(prev));
      setDownPolePointMarkers((prev) => updateMarkerColors(prev));
      setDebrisPointMarkers((prev) => updateMarkerColors(prev));
      setOpenManholePointMarkers((prev) => updateMarkerColors(prev));
      setRoadWorkPointMarkers((prev) => updateMarkerColors(prev));
      setCustomPointMarkers((prev) => updateMarkerColors(prev));
    }
  }, [markerTypes]);

  const getMarkerColor = () => {
    if (
      cfs?.isReadyToDispatch &&
      cfs?.assignmentStatus === AssignmentStatus.NEW
    ) {
      return colors.warning.main;
    }

    return cfs
      ? ASSIGNMENT_STATUS[cfs.assignmentStatus].color
      : colors.error.main;
  };

  useEffect(() => {
    if (
      cfs &&
      cfs.addressInfo?.locationPoints &&
      cfs.addressInfo.locationPoints.length > 0
    ) {
      // The primary pin renders as the main CFS marker; only manual
      // Location points get the translucent secondary marker.
      const lPoint = cfs.addressInfo.locationPoints.find(
        (lp) => !lp.isPrimary,
      ) as ILocationPoint;

      if (!lPoint || !lPoint.point) {
        return;
      }
      const locationMarkers = [
        {
          key: lPoint._id,
          position: {
            lng: lPoint.point.coordinates[0] as number,
            lat: lPoint.point.coordinates[1] as number,
          },
          icon: (
            <IcMarkerCfs
              color={getMarkerColor()}
              style={{ opacity: 0.4 }}
              onClick={(e) => {
                if (isFirstResponderView) {
                  e.stopPropagation();
                  e.preventDefault();
                  window.open(
                    `https://www.google.com/maps?q=${lPoint.point.coordinates[1]},${lPoint.point.coordinates[0]}&z=18`,
                    '_blank',
                  );
                }
              }}
            />
          ),
          info: (
            <Stack direction="row" gap={1} p={1} alignItems="center">
              <Typography fontWeight={400} fontSize={14}>
                {lPoint.point.coordinates[1] +
                  ' , ' +
                  lPoint.point.coordinates[0]}
              </Typography>
              {isDispatcherRole(user as IUser) && (
                <IconButton
                  size="small"
                  sx={{ color: theme.palette.error.main }}
                  onClick={() => {
                    setShowConfirmLocation(true);
                  }}
                >
                  <IcDeleteTrash></IcDeleteTrash>
                </IconButton>
              )}
            </Stack>
          ),
        },
      ];
      if (locationMarkers.length > 0) {
        setLocationPointMarkers(locationMarkers);
      }
    }
  }, [cfs]);

  return (
    <GoogleMap
      markers={markers
        .concat(
          selectedTypes?.fireHydrant &&
            account?.settings?.features?.mapMarkerTypes?.fireHydrant !== false
            ? hydrantPointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.pothole &&
            account?.settings?.features?.mapMarkerTypes?.pothole !== false
            ? potholePointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.roadBlock &&
            account?.settings?.features?.mapMarkerTypes?.roadBlock !== false
            ? roadBlockPointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.flooding &&
            account?.settings?.features?.mapMarkerTypes?.flooding !== false
            ? floodingPointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.limitedVisibility &&
            account?.settings?.features?.mapMarkerTypes?.limitedVisibility !==
              false
            ? limitedVisibilityPointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.deadAnimal &&
            account?.settings?.features?.mapMarkerTypes?.deadAnimal !== false
            ? deadAnimalPointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.roadWork &&
            account?.settings?.features?.mapMarkerTypes?.roadWork !== false
            ? roadWorkPointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.downWires &&
            account?.settings?.features?.mapMarkerTypes?.downWires !== false
            ? downWiresPointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.downPole &&
            account?.settings?.features?.mapMarkerTypes?.downPole !== false
            ? downPolePointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.debris &&
            account?.settings?.features?.mapMarkerTypes?.debris !== false
            ? debrisPointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          selectedTypes?.openManhole &&
            account?.settings?.features?.mapMarkerTypes?.openManhole !== false
            ? openManholePointMarkers.filter(
                (marker) =>
                  selectedTypes.showInactive ||
                  !isExpired(marker.properties?.expirationDateTime),
              )
            : [],
        )
        .concat(
          // Filter custom markers based on their visibility in selectedTypes
          customPointMarkers.filter((marker) => {
            const key = getMarkerTypeKey(marker.type as string);
            const isSelected = selectedTypes[key as keyof typeof selectedTypes];

            return (
              isSelected &&
              (selectedTypes.showInactive ||
                !isExpired(marker.properties?.expirationDateTime))
            );
          }),
        )
        .concat(newCfsAddressMarker || [])
        .concat(locationPointMarkers || [])}
      onMapTypeIdChanged={handleMapTypeIdChange}
      mapTypeId={mapTypeId}
      breadcrumb={breadcrumb}
      showContextMenu={showContextMenu}
      showStreetViewControl={showStreetViewControl}
      handleBoundMapChange={handleBoundMapChange}
      onCloseMarkerInfo={onCloseMarkerInfo}
      showZoomControl={showZoomControl}
      className={className}
      {...rest}
    >
      {rest.children}
      <MapTypeControl />
      <MapMarkerControl
        selectedTypes={selectedTypes}
        handleCheckboxChange={handleCheckboxChange}
      />
      {/* change the background of the info popup to match the theme */}
      <GlobalStyles
        styles={{
          '.gm-style .gm-style-iw-d::-webkit-scrollbar-track, .gm-style .gm-style-iw-d::-webkit-scrollbar-track-piece, .gm-style .gm-style-iw-c, .gm-style .gm-style-iw-t::after, .poi-info-window div, .poi-info-window a':
            {
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
        }}
      />
      <CreateNewMarkerModal
        place={addressMarker || undefined}
        open={newMarkerModal}
        marker={editMarker as IPointLayer}
        onNewMarker={onNewMarkerAdded}
        fetchMapPoints={fetchMapPoints}
        onClose={() => {
          setNewMarkerModal(false);
          setAddressMarker(null);
          setEditMarker(null);
        }}
      />
      <ConfirmationDialog
        title="Delete Marker?"
        description="Warning! You are about to delete this marker, are you sure?"
        confirmText="Delete"
        open={showConfirm}
        onCancel={() => {
          setShowConfirm(false);
        }}
        onConfirm={() => {
          if (editMarker) {
            handleDeleteMarker(editMarker);
            setShowConfirm(false);
          }
        }}
      />
      <ConfirmationDialog
        title="Remove Location Point?"
        description="Are you sure you want to remove the Location Point?"
        confirmText="Yes, Remove"
        open={showConfirmLocation}
        onCancel={() => {
          setShowConfirmLocation(false);
        }}
        onConfirm={() => {
          deleteLocationPoint();
          setShowConfirmLocation(false);
        }}
      />

      <CFSAddressChangeModal
        open={openAddressChangeModal}
        isRemovingAddress={false}
        removedAgencies={pendingRemovedAgencies}
        onClose={handleCancelAddressChange}
        onSubmit={async () => {
          if (pendingLocation) {
            await applyCfsLocation(pendingLocation);
          } else {
            await handleUpdateAddress(placeDetails, addressMarker);
          }
          setOpenAddressChangeModal(false);
        }}
      />

      <CFSAddressJurisdictionModal
        open={openAddressBoundaryModal}
        onClose={() => setOpenAddressBoundaryModal(false)}
        onClearAddress={() => {
          setOpenAddressBoundaryModal(false);
          setAddressMarker(null);
        }}
        onKeepAddress={async () => {
          if (isClickingKeepAddress.current) return;
          isClickingKeepAddress.current = true;

          try {
            setIsSubmittingKeepAddress(true);
            if (pendingLocation) {
              await applyCfsLocation(pendingLocation);
            } else {
              await handleUpdateAddress(placeDetails, addressMarker);
            }
          } finally {
            setIsSubmittingKeepAddress(false);
            setOpenAddressBoundaryModal(false);
            isClickingKeepAddress.current = false;
          }
        }}
        isSubmitting={isSubmittingKeepAddress}
      />
    </GoogleMap>
  );
};
